import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

export type LoginTwoFactorPortal = 'customer' | 'seller';

@Component({
  selector: 'app-login-two-factor-step',
  standalone: true,
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './login-two-factor-step.component.html',
  styleUrl: './login-two-factor-step.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginTwoFactorStepComponent {
  @Input({ required: true }) portal!: LoginTwoFactorPortal;
  @Input() maskedEmail = '';
  @Input() requiresAuthenticator = false;
  @Input() loading = false;
  @Input() errorMessage = '';
  @Input() showResend = true;
  @Input() resendCooldown = 0;

  @Output() verifyCode = new EventEmitter<string>();
  @Output() resend = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  digits = ['', '', '', '', '', ''];

  constructor(private cdr: ChangeDetectorRef) {}

  isCodeComplete(): boolean {
    return this.digits.every((digit) => digit.length === 1);
  }

  resetDigits(): void {
    this.digits = ['', '', '', '', '', ''];
    this.cdr.markForCheck();
  }

  submit(): void {
    const code = this.digits.join('');
    if (code.length !== 6 || this.loading) return;
    this.verifyCode.emit(code);
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('l2fa-overlay')) {
      this.back.emit();
    }
  }

  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    this.digits[index] = value;
    input.value = value;

    if (value && index < 5) {
      const next = input.parentElement?.querySelectorAll('.l2fa-digit')[index + 1] as HTMLInputElement;
      next?.focus();
    }
    this.cdr.markForCheck();
  }

  onDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      const inputs = (event.target as HTMLElement).parentElement?.querySelectorAll('.l2fa-digit');
      const prev = inputs?.[index - 1] as HTMLInputElement;
      prev?.focus();
    }
  }

  onDigitPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    pasted.split('').forEach((char, i) => {
      if (i < 6) this.digits[i] = char;
    });

    const inputs = (event.target as HTMLElement).parentElement?.querySelectorAll('.l2fa-digit');
    const focusIndex = Math.min(pasted.length, 5);
    (inputs?.[focusIndex] as HTMLInputElement)?.focus();
    this.cdr.markForCheck();
  }
}
